import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  Building2,
  ChevronRight,
  FileText,
  FlaskConical,
  Home,
  Layers,
  Mail,
  Menu,
  ShieldCheck,
  Stethoscope,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DEMO_URL, NAV_GROUPS, isExternalHref } from "@/lib/navigation";
import { trackDemoClick } from "@/lib/telemetry";

/**
 * Icons per nav group. Same lucide language as the desktop cards so a group in
 * the mobile menu reads with the same glyph the user meets on the page.
 */
const GROUP_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  platform: Layers,
  solutions: Stethoscope,
  evidence: FlaskConical,
  enterprise: ShieldCheck,
  resources: BookOpen,
  company: Building2,
};

/** Plain-language helper line per group ("so simple a 7-year-old can find it"). */
const GROUP_PLAIN: Record<string, string> = {
  platform: "What we make",
  solutions: "Who it helps",
  evidence: "Proof and rules",
  enterprise: "Safety and setup",
  resources: "Words, guides, and news",
  company: "About us",
};

const SHORTCUTS: { to: string; label: string; hint: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { to: "/", label: "Home", hint: "Start here", Icon: Home },
  { to: "/glossary", label: "Glossary", hint: "What the words mean", Icon: BookOpen },
  { to: "/request-demo", label: "Contact us", hint: "Talk to a person", Icon: Mail },
  { to: "/disclaimers", label: "Disclaimers", hint: "The fine print", Icon: FileText },
];

/**
 * Full site menu for mobile mode. The bottom tab bar covers the five app tabs;
 * this sheet exposes every section of the site so nothing is reachable only on
 * desktop.
 *
 * Dialog accessibility (Radix Dialog underneath, explicitly wired here):
 *  - focus is trapped inside the panel while it is open;
 *  - Escape closes it (and we clear our own open state via `onOpenChange`);
 *  - on open, focus moves to the panel itself so the title and description are
 *    announced before the link list instead of landing mid-menu;
 *  - on close, focus returns to the hamburger trigger, including when the sheet
 *    closes because a link changed the route.
 */
export const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const { pathname } = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Always close the sheet when the route changes.
  useEffect(() => setOpen(false), [pathname]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label="Open site menu"
          aria-haspopup="dialog"
          aria-expanded={open}
          className="inline-flex min-h-11 min-w-11 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-sm font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <Menu className="size-5" aria-hidden="true" />
          <span>Menu</span>
        </button>
      </SheetTrigger>
      <SheetContent
        ref={panelRef}
        side="right"
        aria-label="Site menu"
        tabIndex={-1}
        onOpenAutoFocus={(event) => {
          // Land on the panel container, not the first accordion trigger.
          event.preventDefault();
          panelRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          // Return focus to the hamburger even when the close came from a
          // route change rather than Escape or the close button.
          event.preventDefault();
          triggerRef.current?.focus();
        }}
        onEscapeKeyDown={close}
        className="flex w-[min(22rem,92vw)] max-w-sm flex-col overflow-y-auto px-4 focus-visible:outline-none"
      >

        <SheetHeader className="text-left">
          <SheetTitle className="text-lg">Menu</SheetTitle>
          <SheetDescription className="text-xs leading-relaxed">
            Tap a heading to see what is inside.
          </SheetDescription>
        </SheetHeader>

        <nav aria-label="Site" className="mt-4">
          <ul className="flex flex-col gap-2">
            {SHORTCUTS.map(({ to, label, hint, Icon }) => (
              <li key={to}>
                <Link
                  to={to}
                  onClick={close}
                  aria-current={pathname === to ? "page" : undefined}
                  className="flex min-h-14 items-center gap-2.5 rounded-2xl border border-border bg-card px-2.5 py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:gap-3 sm:px-3"
                >
                  <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-primary sm:size-10">
                    <Icon className="size-[18px] sm:size-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-[13px] font-semibold leading-snug text-foreground sm:text-sm">
                      {label}
                    </span>
                    <span className="block break-words text-[11px] leading-snug text-muted-foreground sm:text-xs">
                      {hint}
                    </span>
                  </span>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground sm:size-5" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>


          <Accordion type="multiple" className="mt-5 w-full">
            {NAV_GROUPS.map((group) => {
              const Icon = GROUP_ICONS[group.id] ?? Layers;
              return (
                <AccordionItem key={group.id} value={group.id}>
                  <AccordionTrigger className="gap-2 py-3 text-left text-[15px] font-semibold hover:no-underline sm:text-base">
                    <span className="flex min-w-0 flex-1 items-center gap-2.5 sm:gap-3">
                      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-primary sm:size-10">
                        <Icon className="size-[18px] sm:size-5" aria-hidden="true" />
                      </span>
                      <span className="min-w-0">
                        <span className="block break-words leading-snug">{group.label}</span>
                        <span className="block break-words text-[11px] font-normal leading-snug text-muted-foreground sm:text-xs">
                          {GROUP_PLAIN[group.id] ?? group.blurb}
                        </span>
                      </span>
                    </span>
                  </AccordionTrigger>

                  <AccordionContent>
                    <ul aria-label={group.label} className="flex flex-col pl-11 sm:pl-[52px]">
                      {group.items.map((item) =>
                        isExternalHref(item.href) ? (
                          <li key={item.href}>
                            <a
                              href={item.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex min-h-12 items-center gap-1.5 rounded-md py-1.5 pr-1 text-[13px] font-medium leading-snug text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:text-sm"
                            >
                              <span className="min-w-0 break-words">{item.label}</span>
                              <ArrowUpRight className="size-4 shrink-0" aria-hidden="true" />
                              <span className="sr-only">(opens in a new tab)</span>
                            </a>
                          </li>
                        ) : (
                          <li key={item.href}>
                            <Link
                              to={item.href}
                              onClick={close}
                              aria-current={pathname === item.href ? "page" : undefined}
                              className="flex min-h-12 items-center justify-between gap-2 rounded-md py-1.5 pr-1 text-[13px] font-medium leading-snug text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:text-sm"
                            >
                              <span className="min-w-0 break-words">{item.label}</span>
                              <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                            </Link>
                          </li>
                        ),
                      )}
                    </ul>


                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          <div className="mt-6 pb-6">
            <Button asChild size="lg" className="w-full">
              <a href={DEMO_URL} onClick={() => trackDemoClick("mobile_menu")}>
                Book a Demo
              </a>
            </Button>
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
};
